/**
 * DealTagBadge - Badge visual para exibir uma tag
 * 
 * Versão: 1.1.0
 * - Suporte a tamanhos sm/md
 * - Cor dinâmica baseada na propriedade
 * - Opção de remover
 * - v1.1.0: Truncamento interno do nome + title para hover
 * 
 * RELEASE: 2026-02-01
 */

import React from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

// Props do componente
interface DealTagBadgeProps {
  /** Nome da tag */
  name: string;
  /** Cor em hexadecimal */
  color: string;
  /** Tamanho do badge */
  size?: 'sm' | 'md';
  /** Callback para remover (mostra X se definido) */
  onRemove?: () => void;
  /** Classes adicionais */
  className?: string;
}

/**
 * Calcula cor de texto baseada na luminosidade do background
 */
function getContrastColor(hexColor: string): string {
  // Remove # se presente
  const hex = hexColor.replace('#', '');
  
  // Converte para RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  // Calcula luminosidade
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  return luminance > 0.5 ? '#000000' : '#ffffff';
}

export function DealTagBadge({
  name,
  color,
  size = 'sm',
  onRemove,
  className,
}: DealTagBadgeProps) {
  const textColor = getContrastColor(color);
  
  return (
    <span
      className={cn(
        // v1.1.0: Adicionado min-w-0 e max-w-full para permitir truncamento
        'inline-flex items-center gap-1 rounded-full font-medium transition-all min-w-0 max-w-full',
        size === 'sm' && 'px-1 py-0 text-[9px] leading-tight',
        size === 'md' && 'px-1.5 py-px text-[10px] leading-tight',
        onRemove && 'pr-1',
        className
      )}
      style={{
        backgroundColor: color,
        color: textColor,
      }}
      // v1.1.0: Title para mostrar nome completo no hover
      title={name}
    >
      {/* v1.1.0: Nome truncado com reticências quando muito longo */}
      <span className="truncate min-w-0 py-0 my-0">{name}</span>
      
      {onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="ml-0.5 rounded-full p-0.5 hover:bg-black/10 transition-colors shrink-0"
          aria-label={`Remover tag ${name}`}
        >
          <X className={cn(size === 'sm' ? 'h-2.5 w-2.5' : 'h-3 w-3')} />
        </button>
      )}
    </span>
  );
}
